import { useTranslations } from 'next-intl';
import isMobilePhone from 'validator/lib/isMobilePhone';
import { array, boolean, mixed, number, object, setLocale, string } from 'yup';

/**
 * Custom hook to build a Yup validation schema.
 *
 * @returns {object} - An object of transforms that can be used to build a Yup validation schema.
 *
 * @see https://github.com/jquense/yup
 */

export const useValidation = () => {
  const t = useTranslations('forms.validation');

  setLocale({
    mixed: {
      required: t('required'),
      notType: ({ type }) => {
        switch (type) {
          case 'string':
            return t('type.string');
          case 'number':
            return t('type.number');
          default:
            return t('type.default');
        }
      },
    },
    string: {
      min: ({ min }) => t('min.text', { min }),
      max: ({ max }) => t('max.text', { max }),
    },
    array: {
      min: ({ min }) => t('min.item', { min }),
      max: ({ max }) => t('max.item', { max }),
    },
  });

  return {
    input: string().min(2).max(50).required(),
    textfield: string().min(2).max(50),
    textarea: string().min(2).max(255),
    select: (values: string[]) => string().required().oneOf(values),
    checkbox: boolean(),
    number: number(),
    shape: (schema = {}) => object().shape(schema),
    object,
    email: string().email().required(),
    phone: string()
      .test('is-mobile-phone', t('phone'), (value) => {
        if (!value || value.trim() === '') return true;
        return isMobilePhone(value.trim().replace(/\s+/g, ''), 'any', { strictMode: false });
      })
      .required(t('required')),
    consent: boolean().required().oneOf([true], t('consent')),
    array: array(),
    file: (formats: string[] = [], maxFileSize = 9216, required = false) =>
      mixed()
        .test('required', t('required'), (file) => (required ? file instanceof File : true))
        .test('fileMax', t('file.max'), (file) => {
          if (file instanceof File) {
            return file.size <= maxFileSize * 1024;
          }
          return !required;
        })
        .test('fileFormat', t('file.format'), (file) => {
          if (file instanceof File) {
            const extension = file.name.split('.').pop()?.toLowerCase();
            return formats.length === 0 || formats.includes(extension || '');
          }
          return !required;
        }),
  };
};
